<template>
  <aside class="sidebar" :class="{ 'collapsed': collapsed }">
    <!-- Logo Section -->
    <div class="sidebar-header">
      <router-link to="/" class="logo-row">
        <div class="logo">
          <img src="@/assets/images/logo.png" alt="PassQR Logo" />
        </div>
        <div v-show="!collapsed" class="app-info">
          <span class="app-title"><span class="track-text">PassQR</span></span>
          <span class="app-subtitle">{{ t('sidebar.systemTitle', 'Personel Takip Sistemi') }}</span>
        </div>
      </router-link>
      <div v-show="!collapsed" class="company-row" v-if="companyName">
        <span class="company-name">{{ companyName }}</span>
      </div>
    </div>
    
    <nav class="nav">      
      <div v-for="(item, index) in filteredMenuItems" :key="index" class="nav-group">
        <!-- Başlık ve alt menüleri olan öğeler için -->
        <div v-if="item.children" class="nav-item-with-children">
          <!-- Collapsed durumunda popover ile alt menüleri göster -->
          <el-popover
            v-if="collapsed"
            placement="right-start"
            :width="200"
            trigger="hover"
            popper-class="sidebar-submenu-popover"
          >
            <template #reference>
              <div class="nav-item parent-item">
                <div class="nav-item-content">
                  <i :class="item.icon"></i>
                </div>
              </div>
            </template>
            <div class="popover-submenu">
              <div class="popover-submenu-title">{{ item.title }}</div>
              <router-link
                v-for="child in item.children"
                :key="child.path"
                :to="child.path"
                class="popover-submenu-item"
                @click="closePopover"
              >
                <i :class="child.icon"></i>
                <span>{{ child.title }}</span>
              </router-link>
            </div>
          </el-popover>
          
          <!-- Normal durumda (açık sidebar) -->
          <template v-else>
            <el-tooltip :content="item.title" placement="right" :disabled="true">
              <div 
                class="nav-item parent-item" 
                :class="{ 'expanded': expandedMenus.includes(index) }"
                @click="toggleMenu(index)"
              >
                <div class="nav-item-content">
                  <i :class="item.icon"></i>
                  <span>{{ item.title }}</span>
                </div>
                <i class="fas" :class="expandedMenus.includes(index) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </div>
            </el-tooltip>
            
            <div class="submenu" v-show="expandedMenus.includes(index)">
              <router-link
                v-for="child in item.children"
                :key="child.path"
                :to="child.path"
                class="nav-item submenu-item"
              >
                <i :class="child.icon"></i>
                <span>{{ child.title }}</span>
              </router-link>
            </div>
          </template>
        </div>
        
        <!-- Normal menü öğeleri için -->
        <el-tooltip v-else :content="item.title" placement="right" :disabled="!collapsed">
          <router-link
            :to="item.path"
            class="nav-item"
          >
            <div class="nav-item-content">
              <i :class="item.icon"></i>
              <span v-show="!collapsed">{{ item.title }}</span>
            </div>
          </router-link>
        </el-tooltip>
      </div>
    </nav>
    
    <!-- Footer -->
    <div class="sidebar-footer">
      <el-tooltip :content="t('sidebar.logout', 'Oturumu Kapat')" placement="right" :disabled="!collapsed">
        <div class="footer-logout" @click="handleLogout">
          <i class="fas fa-sign-out-alt"></i>
          <span v-show="!collapsed">{{ t('sidebar.logout', 'Oturumu Kapat') }}</span>
        </div>
      </el-tooltip>
      <div v-show="!collapsed" class="footer-version">
        v1.0.0
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import permissionService from '@/services/permission.service';
import { useAuthStore } from '@/stores/auth';
import { useLanguageStore } from '@/stores/language';
import api from '@/api';

const props = defineProps<{
  collapsed?: boolean
}>()

const router = useRouter()
const auth = useAuthStore()
const languageStore = useLanguageStore()

// Company settings for labor law country check
const companySettings = ref<{ labor_law_country?: string }>({ labor_law_country: 'tr' })
const { currentLanguage, translations } = storeToRefs(languageStore)
const { t } = languageStore

const companyName = computed(() => {
  return auth.user?.company_name || auth.user?.name || ''
})

const handleLogout = () => {
  auth.logout()
  router.push('/login')
}

interface MenuItem {
  title: string
  path: string
  icon: string
  children?: MenuItem[]
  permission?: string
  laborLawCountry?: string // Sadece belirli ülke ayarında göster (örn: 'tr' = sadece KVKK için)
}

const expandedMenus = ref<number[]>([]);

const toggleMenu = (index: number) => {
  // Collapsed durumunda alt menüleri açma (popover ile gösterilecek)
  if (props.collapsed) {
    return;
  }
  const position = expandedMenus.value.indexOf(index);
  if (position !== -1) {
    expandedMenus.value.splice(position, 1);
  } else {
    expandedMenus.value.push(index);
  }
};

const closePopover = () => {
  // Popover'ı kapatmak için (isteğe bağlı, otomatik kapanabilir)
};

// Menu items with translation keys
const getMenuItems = (): MenuItem[] => [
  {
    title: t('sidebar.dashboard', 'Dashboard'),
    path: '/',
    icon: 'fas fa-home'
  },
  {
    title: t('sidebar.branch', 'Şube'),
    path: '/branches',
    icon: 'fas fa-building',
    permission: 'manage_branches'
  },
  {
    title: t('sidebar.kiosk', 'QR Kiosk'),
    path: '/kiosk',
    icon: 'fas fa-qrcode',
    permission: 'manage_branches'
  },
  {
    title: t('sidebar.department', 'Departman'),
    path: '/departments',
    icon: 'fas fa-sitemap',
    permission: 'manage_departments'
  },  
  {
    title: t('sidebar.personnel', 'Personel'),
    path: '#',
    icon: 'fas fa-users',
    children: [
      {
        title: t('sidebar.personnelList', 'Personel Listesi'),
        path: '/employees',
        icon: 'fas fa-users',
        permission: 'view_employees'
      },
      {
        title: t('sidebar.personnelMovements', 'Personel Hareketleri'),
        path: '/employee-hire-resign',
        icon: 'fas fa-exchange-alt',
        permission: 'view_employees'
      },
      {
        title: t('sidebar.birthdays', 'Doğum Günleri'),
        path: '/birthdays',
        icon: 'fas fa-birthday-cake',
        permission: 'view_employees'
      },
      {
        title: t('sidebar.importPersonnel', 'Personel Aktar'),
        path: '/employee-import',
        icon: 'fas fa-upload',
        permission: 'manage_employees'
      },
      {
        title: t('sidebar.importLeaveBalance', 'İzin Bakiye Aktar'),
        path: '/leave-balance-import',
        icon: 'fas fa-upload',
        permission: 'manage_employees'
      },
    ]
  },
  {
    title: t('sidebar.registeredDevices', 'Kayıtlı Cihazlar'),
    path: '/devices',
    icon: 'fas fa-mobile-alt',
    permission: 'view_devices'
  },
  {
    title: t('sidebar.shiftManagement', 'Vardiya Yönetimi'),
    path: '#',
    icon: 'fas fa-calendar-alt',
    permission: 'manage_shifts',
    children: [
      {
        title: t('sidebar.shiftList', 'Vardiya Listesi'),
        path: '/shifts',
        icon: 'fas fa-calendar-alt',
        permission: 'manage_shifts'
      },
      {
        title: t('sidebar.shiftRotations', 'Vardiya Rotasyonları'),
        path: '/shift-rotations',
        icon: 'fas fa-sync',
        permission: 'manage_shifts'
      },
      {
        title: t('sidebar.shiftPersonnelManagement', 'Vardiya Personel Yönetimi'),
        path: '/shift-employee-management',
        icon: 'fas fa-user-clock',
        permission: 'manage_shifts'
      },
      {
        title: t('sidebar.shiftCalendar', 'Vardiya Takvimi'),
        path: '/shift-calendar',
        icon: 'fas fa-calendar-week',
        permission: 'manage_shifts'
      },
      {
        title: t('sidebar.dailyShiftPlanning', 'Günlük Vardiya Planlama'),
        path: '/daily-shift-planning',
        icon: 'fas fa-calendar-day',
        permission: 'manage_shifts'
      }
    ]
  },
  {
    title: t('sidebar.leaveManagement', 'İzin Yönetimi'),
    path: '#',
    icon: 'fas fa-calendar-check',
    children: [
      {
        title: t('sidebar.dayOffChange', 'İzin Günü Değişikliği'),
        path: '/day-off-change',
        icon: 'fas fa-exchange-alt',
        permission: 'approve_day_off'
      },
      {
        title: t('sidebar.leaveRequests', 'İzin Talepleri'),
        path: '/leave-requests-list',
        icon: 'fas fa-list',
        permission: 'approve_leave'
      },
      {
        title: t('sidebar.leaveTypes', 'İzin Türleri'),
        path: '/leave-types',
        icon: 'fas fa-tags',
      },
      {
        title: t('sidebar.personnelLeaveBalance', 'Personel İzin Bakiye'),
        path: '/leave-balance-management',
        icon: 'fas fa-balance-scale',
        permission: 'manage_leave_balance'
      },
      {
        title: t('sidebar.leaveBalanceByType', 'İzin Türüne Göre Bakiye'),
        path: '/leave-balance-by-type',
        icon: 'fas fa-list-alt',
        permission: 'manage_leave_balance'
      },
      {
        title: t('sidebar.leavePlanning', 'İzin Planlama'),
        path: '/leave-planning',
        icon: 'fas fa-calendar-plus',
        permission: 'view_leave_planning'
      },
      {
        title: t('sidebar.leaveEntitlements', 'İzin Hakedişleri'),
        path: '/leave-entitlements',
        icon: 'fas fa-gift',
        permission: 'manage_leave_entitlements'
      },
      {
        title: t('sidebar.leaveUsageMovements', 'İzin Kullanım Hareketleri'),
        path: '/leave-usage-movements',
        icon: 'fas fa-chart-line',
        permission: 'approve_leave'
      }
    ]
  },
  {
    title: t('sidebar.assignments', 'Görevlendirme'),
    path: '#',
    icon: 'fas fa-exchange-alt',
    children: [
      {
        title: t('sidebar.branchAssignments', 'Şube Görevlendirmeleri'),
        path: '/branch-assignments',
        icon: 'fas fa-exchange-alt',
        permission: 'manage_branch_assignments'
      },
      {
        title: t('sidebar.dutyPeriods', 'Görevli Zamanlar'),
        path: '/duty-periods',
        icon: 'fas fa-briefcase',
        permission: 'manage_duty_periods'
      }
    ]
  },
  {
    title: t('sidebar.advanceRequests', 'Avans Talepleri'),
    path: '/advance-requests-list',
    icon: 'fas fa-money-bill',
    permission: 'approve_advance'
  },
  {
    title: t('sidebar.attendance', 'Giriş/Çıkış'),
    path: '#',
    icon: 'fas fa-calendar-check',        
    children: [
      {
        title: t('sidebar.attendanceRecords', 'Giriş/Çıkış Kayıtları'),
        path: '/activity-list',
        icon: 'fas fa-calendar-check',
        permission: 'view_own_attendance',
      },
      {
        title: t('sidebar.personnelStatusReport', 'Personel Durum Raporu'),
        path: '/reports/employee-status',
        icon: 'fas fa-user-check',
        permission: 'view_own_attendance',
      },
      {
        title: t('sidebar.manualEntryRequests', 'Manuel Giriş Talepleri'),
        path: '/manual-entry-requests',
        icon: 'fas fa-clipboard-check',
        permission: 'view_employees'
      }
    ]
  },
  {
    title: t('sidebar.notifications', 'Bildirimler'),
    path: '/notifications',
    icon: 'fas fa-bell',
    permission: 'manage_notifications'
  },
  {
    title: t('sidebar.events', 'Etkinlikler'),
    path: '/events',
    icon: 'fas fa-calendar-check',
    permission: 'view_events'
  },
  {
    title: t('sidebar.assetManagement', 'Zimmet Yönetimi'),
    path: '#',
    icon: 'fas fa-boxes',
    permission: 'view_assets',
    children: [
      {
        title: t('sidebar.assets', 'Zimmetler'),
        path: '/assets',
        icon: 'fas fa-box',
        permission: 'view_assets'
      },
      {
        title: t('sidebar.upcomingMaintenance', 'Yaklaşan Bakımlar'),
        path: '/upcoming-maintenance',
        icon: 'fas fa-calendar-check',
        permission: 'view_maintenance'
      },
      {
        title: t('sidebar.faultRecords', 'Arıza Kayıtları'),
        path: '/faults',
        icon: 'fas fa-exclamation-triangle',
        permission: 'view_faults'
      }
    ]
  },
  {
    title: t('sidebar.serviceManagement', 'Servis Yönetimi'),
    path: '#',
    icon: 'fas fa-bus',
    permission: 'manage_service_transport',
    children: [
      {
        title: t('sidebar.routes', 'Güzergahlar'),
        path: '/service-routes',
        icon: 'fas fa-route',
        permission: 'manage_service_transport'
      },
      {
        title: t('sidebar.serviceVehicles', 'Servis Araçları'),
        path: '/service-vehicles',
        icon: 'fas fa-shuttle-van',
        permission: 'manage_service_transport'
      },
      {
        title: t('sidebar.scheduleAndAssignments', 'Program ve Atamalar'),
        path: '/service-assignments',
        icon: 'fas fa-user-clock',
        permission: 'manage_service_transport'
      }
    ]
  },
  {
    title: t('sidebar.reports', 'Raporlar'),
    path: '#',
    icon: 'fas fa-chart-bar',
    children: [
      {
        title: t('sidebar.dailyAttendanceReport', 'Günlük Devam Raporu'),
        path: '/reports/daily-attendance',
        icon: 'fas fa-calendar-day',
        permission: 'view_reports_daily_attendance'
      },
      {
        title: t('sidebar.monthlyTimesheet', 'Aylık Puantaj'),
        path: '/reports/monthly-summary',
        icon: 'fas fa-chart-line',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.monthlyTimesheetHours', 'Aylık Puantaj (Saat)'),
        path: '/reports/monthly-time',
        icon: 'fas fa-clock',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.monthlyAttendanceReport', 'Aylık Giriş-Çıkış Raporu'),
        path: '/reports/employee-monthly-attendance',
        icon: 'fas fa-user-clock',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.weeklyWorkingHoursReport', 'Haftalık Çalışma Saati Raporu'),
        path: '/reports/weekly-employee-attendance',
        icon: 'fas fa-calendar-week',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.dailyWorkHoursReport', 'Günlük Çalışma Saati Raporu'),
        path: '/reports/daily-work-hours',
        icon: 'fas fa-business-time',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.dailyAllEmployeesWorkHoursReport', 'Günlük Tüm Personel Çalışma Saati Raporu'),
        path: '/reports/daily-all-employees-work-hours',
        icon: 'fas fa-business-time',
        permission: 'view_reports_monthly_summary'
      },
      {
        title: t('sidebar.leaveBalanceReport', 'İzin Bakiye Raporu'),
        path: '/reports/leave-balance',
        icon: 'fas fa-hourglass-half',
        permission: 'view_reports_leave_balance'
      },
      {
        title: t('sidebar.absenceReport', 'Devamsızlık Raporu'),
        path: '/reports/absence',
        icon: 'fas fa-user-times',
        permission: 'view_reports_absence'
      },
      {
        title: t('sidebar.overtimeReport', 'Fazla Mesai Raporu'),
        path: '/reports/overtime',
        icon: 'fas fa-clock',
        permission: 'view_reports_overtime'
      },
      {
        title: t('sidebar.holidayWorkCompensationReport', 'Tatil Çalışması Hakedişi Raporu'),
        path: '/reports/holiday-work-compensation',
        icon: 'fas fa-calendar-plus',
        permission: 'view_reports_holiday_work_compensation'
      },
      {
        title: t('sidebar.mealCardReport', 'Yemek Kartı Raporu'),
        path: '/reports/dinner-card',
        icon: 'fas fa-utensils',
        permission: 'view_reports_dinner_card'
      }
    ]
  },
  {
    title: t('sidebar.breakReports', 'Mola Raporları'),
    path: '#',
    icon: 'fas fa-coffee',
    permission: 'view_break_time',
    children: [
      {
        title: t('sidebar.breakMovements', 'Mola Hareketleri'),
        path: '/reports/breaks',
        icon: 'fas fa-list',
        permission: 'view_break_time',
      },
      {
        title: t('sidebar.breakSummaryReport', 'Mola Özet Raporu'),
        path: '/reports/break-summary',
        icon: 'fas fa-chart-pie',
        permission: 'view_break_time',
      }
    ]
  },
  {
    title: t('sidebar.rolesAndPermissions', 'Roller ve Yetkiler'),
    path: '/roles-permissions',
    icon: 'fas fa-user-shield',
    permission: 'manage_roles'
  },
  {
    title: t('sidebar.webPanelUsers', 'Web Panel Kullanıcıları'),
    path: '/users',
    icon: 'fas fa-user-shield',
    permission: 'manage_users'
  },
  {
    title: t('sidebar.holidays', 'Tatiller'),
    path: '/holidays',
    icon: 'fas fa-calendar-alt',
    permission: 'manage_holidays'
  },
  {
    title: t('sidebar.dailyMenu', 'Günlük Yemek Menüsü'),
    path: '/daily-menu',
    icon: 'fas fa-utensils',
    permission: 'manage_daily_menu'
  },
  {
    title: t('sidebar.suspiciousActivities', 'Şüpheli İşlemler'),
    path: '/suspicious-activities',
    icon: 'fas fa-exclamation-triangle',
    permission: 'view_suspicious_activities'
  },
  {
    title: t('sidebar.suggestions', 'Fikir ve Öneriler'),
    path: '/suggestions',
    icon: 'fas fa-lightbulb'
  },
  {
    title: t('sidebar.procedures', 'Prosedürler'),
    path: '/procedures',
    icon: 'fas fa-folder-tree',
    permission: 'manage_procedures'
  },
  {
    title: t('sidebar.settingsAndDefinitions', 'Ayarlar ve Tanımlar'),
    path: '#',
    icon: 'fas fa-cog',
    children: [
      {
        title: t('sidebar.generalSettings', 'Genel Ayarlar'),
        path: '/settings',
        icon: 'fas fa-cog',
        permission: 'manage_settings'
      },
      {
        title: t('sidebar.breakTypes', 'Mola Tipleri'),
        path: '/break-types',
        icon: 'fas fa-coffee',
        permission: 'manage_break_time'
      },
      {
        title: t('sidebar.beaconDevices', 'Beacon Cihazları'),
        path: '/beacons',
        icon: 'fas fa-broadcast-tower',
        permission: 'manage_break_time'
      }
    ]
  },
  {
    title: t('sidebar.subscription', 'Abonelik'),
    path: '/subscription',
    icon: 'fas fa-credit-card',
    permission: 'manage_subscription'
  },
  {
    title: t('sidebar.kvkkConsents', 'KVKK Onayları'),
    path: '/kvkk-consents',
    icon: 'fas fa-shield-alt',
    laborLawCountry: 'tr' // Sadece Türkiye için göster
  }
]

const menuItems = ref<MenuItem[]>(getMenuItems())

const filteredMenuItems = ref<MenuItem[]>([]);

// Watch for language changes and update menu items
// Dil veya çeviriler değiştiğinde menüyü güncelle
watch([currentLanguage, translations], () => {
  menuItems.value = getMenuItems()
  updateFilteredMenuItems()
}, { deep: true })

const fetchCompanySettings = async () => {
  try {
    const response = await api.get('/settings')
    if (response.data) {
      companySettings.value = response.data
    }
  } catch (error) {
    console.error('Error fetching company settings:', error)
  }
}

const updateFilteredMenuItems = async () => {
  const result: MenuItem[] = [];
  for (const item of menuItems.value) {
    // Labor law country kontrolü (örn: KVKK sadece TR için)
    if (item.laborLawCountry && item.laborLawCountry !== companySettings.value.labor_law_country) {
      continue; // Bu menü öğesini atla
    }
    
    // Ana menü öğesi için yetki kontrolü
    if (!item.permission || await permissionService.hasPermission(item.permission)) {
      // Eğer alt menüler varsa, onları da filtrele
      if (item.children) {
        const filteredChildren = [];
        for (const child of item.children) {
          // Alt menü için de labor law kontrolü
          if (child.laborLawCountry && child.laborLawCountry !== companySettings.value.labor_law_country) {
            continue;
          }
          if (!child.permission || await permissionService.hasPermission(child.permission)) {
            filteredChildren.push(child);
          }
        }
        // Eğer filtrelenmiş alt menüler varsa, ana menüyü ve filtrelenmiş alt menüleri ekle
        if (filteredChildren.length > 0) {
          result.push({
            ...item,
            children: filteredChildren
          });
        }
      } else {
        // Alt menüsü olmayan öğeleri direkt ekle
        result.push(item);
      }
    }
  }
  filteredMenuItems.value = result;
}

// Call the function in onMounted
onMounted(async () => {
  await fetchCompanySettings()
  await updateFilteredMenuItems()
});
</script>

<style scoped>
.sidebar {
  width: 240px;
  height: 100vh;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 60%, #020617 100%);
  border-right: 1px solid rgba(30, 41, 59, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 101;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.3);
}

.sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(ellipse at 30% 0%, rgba(56, 189, 248, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 100%, rgba(249, 115, 22, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.sidebar.collapsed {
  width: 64px;
}

.sidebar-header {
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
  gap: 8px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  position: relative;
}

.sidebar.collapsed .sidebar-header {
  padding: 12px 8px;
  align-items: center;
}

.logo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.logo {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.sidebar.collapsed .logo {
  width: 40px;
  height: 40px;
}

.logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.app-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.app-title {
  font-size: 16px;
  font-weight: 600;
  color: #f1f5f9;
  line-height: 1.2;
  white-space: nowrap;
  letter-spacing: -0.01em;
}

.app-subtitle {
  font-size: 10px;
  color: #cbd5e1;
  font-weight: 400;
  letter-spacing: 0.02em;
}

.track-text {
  color: #fb923c;
}

.company-row {
  padding-left: 0;
}

.company-name {
  font-size: 11px;
  color: #e2e8f0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 170px;
  letter-spacing: 0.01em;
}

.nav {
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;
  position: relative;
}

.nav-group {
  display: flex;
  flex-direction: column;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #e2e8f0;
  text-decoration: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 200 !important;
  transition: all 0.2s ease;
  cursor: pointer;
  letter-spacing: 0.01em;
}

.sidebar.collapsed .nav-item {
  padding: 12px;
  justify-content: center;
}

.nav-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.parent-item {
  justify-content: space-between;
}

.nav-item:hover {
  background: rgba(148, 163, 184, 0.1);
  color: #ffffff;
}

.nav-item.router-link-active {
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.15) 0%, rgba(56, 189, 248, 0.05) 100%);
  color: #38bdf8;
  font-weight: 400 !important;
  border-left: 2px solid #38bdf8;
  margin-left: -2px;
}

.nav-item i {
  font-size: 12px;
  width: 20px;
  opacity: 0.85;
}

.submenu {
  margin-left: 12px;
  margin-top: 4px;
  margin-bottom: 4px;
  border-left: 1px solid rgba(148, 163, 184, 0.15);
}

.sidebar.collapsed .submenu {
  display: none;
}

.submenu-item {
  padding: 10px 16px;
  font-size: 12.5px;
  font-weight: 300;
  color: #cbd5e1;
}

/* Popover alt menü stilleri */
.popover-submenu {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.popover-submenu-title {
  font-weight: 300;
  font-size: 12px;
  color: #475569;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  letter-spacing: 0.01em;
}

.popover-submenu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #64748b;
  text-decoration: none;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 300;
  transition: all 0.2s ease;
  cursor: pointer;
  letter-spacing: 0.01em;
}

.popover-submenu-item:hover {
  background: rgba(255, 255, 255, 0.8);
  color: #475569;
}

.popover-submenu-item.router-link-active {
  background: rgba(249, 115, 22, 0.08);
  color: #ea580c;
}

.popover-submenu-item i {
  font-size: 12px;
  width: 18px;
  opacity: 0.8;
}

/* Popover genel stilleri */
:deep(.sidebar-submenu-popover) {
  padding: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
  background: rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  position: relative;
}

.footer-logout {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 400;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.01em;
}

.footer-logout:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.footer-logout i {
  font-size: 12px;
  width: 18px;
  opacity: 0.8;
}

.sidebar.collapsed .footer-logout {
  justify-content: center;
  padding: 10px;
}

.footer-version {
  font-size: 9px;
  color: #64748b;
  text-align: center;
  margin-top: 8px;
  font-weight: 400;
  letter-spacing: 0.02em;
}

@media (max-width: 768px) {
  .sidebar {
    width: 60px;
  }

  .nav-item span {
    display: none;
  }

  .nav-item {
    padding: 12px;
    justify-content: center;
  }

  .nav-item i.fa-chevron-down,
  .nav-item i.fa-chevron-right {
    display: none;
  }

  .submenu {
    position: absolute;
    left: 60px;
    margin-left: 0;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 100;
    min-width: 180px;
  }
  
  .submenu-item {
    padding: 10px 12px;
  }
  
  .submenu-item span {
    display: inline;
  }
}
</style>